/**
 * ProjectContentSections — S4 Glassmorphic collapsible sections.
 *
 * Each section card shows: icon badge + title + 2-line body preview.
 * Clicking expands to show full content. Editors get inline edit
 * controls for title, body, and delete.
 *
 * Icon mapping:
 *   - Predefined titles → curated SVG icons
 *   - Custom/freeform titles → generic document icon
 *   - "Problems Addressed" → red-tinted (Problem pillar)
 *   - "Solutions Used" → green-tinted (Solution pillar)
 */

import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { generateHTML } from '@tiptap/core';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import i18n from '../../i18n/config';
import { useApiProjectSections } from '../../api/useApiProjectSections';
import type {
  ContentSectionEntityType,
  ProjectContentSectionResponse,
} from '../../api/projectContentSections';
import { getSectionExtensions } from './sectionEditorExtensions';
import styles from './ProjectContentSections.module.css';
import SectionEditorModal from '../SectionEditorModal/SectionEditorModal';
// Section bodies legitimately contain YouTube/allowlisted iframe embeds, so the
// rendered HTML is sanitized with the embed-preserving variant (still strips
// scripts/handlers). Plain sanitizeRichText would drop the video iframes.
import { sanitizeEmbed } from '../../utils/sanitizeHtml';

/* ------------------------------------------------------------------ */
/*  Suggested section titles (generic, not project-specific)           */
/* ------------------------------------------------------------------ */

const SUGGESTED_TITLES = [
  'overview',
  'purpose',
  'problemsAddressed',
  'solutionsUsed',
  'methodology',
  'bestPractices',
  'lessonsLearned',
  'references',
] as const;

/** Maps a predefined suggested-title key to its typed section_type
 *  (DEV-903 vocabulary, migrations 224/226). Keys with no vocabulary
 *  entry (purpose, references) fall through to the 'custom' default.
 *
 *  The 'overview' mapping is load-bearing: it's what lets a freshly
 *  added Overview section be picked up by the dedicated top slot
 *  (slot="overview") that renders above Team on both About surfaces
 *  (mini + full). Without a stable section_type, Overview would only be
 *  identifiable by its (localized, renameable) title. */
const TITLE_KEY_SECTION_TYPE: Record<string, string> = {
  overview: 'overview',
  problemsAddressed: 'problem_addressed',
  solutionsUsed: 'solution_used',
  methodology: 'methodologies',
  bestPractices: 'best_practice',
  lessonsLearned: 'lesson_learned',
};

/* ------------------------------------------------------------------ */
/*  Predefined title detection                                         */
/* ------------------------------------------------------------------ */

/** Build a Set of all known predefined titles across all locales.
 *  Used to determine if a section title is editable (custom) or
 *  locked (predefined). Computed once at module load. */
const PREDEFINED_TITLES: Set<string> = (() => {
  const tFn = i18n.t.bind(i18n) as (key: string, opts?: Record<string, unknown>) => string;
  const titles = new Set<string>();
  const langs = ['en', 'ar', 'de', 'es', 'fr', 'ru', 'sv', 'uk'];
  for (const key of SUGGESTED_TITLES) {
    for (const lng of langs) {
      const val = tFn(`projectDetail.about.${key}`, { lng, ns: 'pages' });
      if (val) titles.add(val.toLowerCase().trim());
    }
  }
  return titles;
})();

/** Returns true if the section title matches a predefined suggested title. */
function isPredefinedTitle(title: string): boolean {
  return PREDEFINED_TITLES.has(title.toLowerCase().trim());
}

/* ------------------------------------------------------------------ */
/*  Icon SVG components                                                */
/* ------------------------------------------------------------------ */

/** Lightbulb — Project Overview */
function IconOverview() {
  return (
    <svg viewBox="0 0 24 24">
      <path d="M9 21h6v-1H9v1zm3-19a7 7 0 0 0-4 12.72V17a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-2.28A7 7 0 0 0 12 2zm2.5 11.59l-.5.34V16h-4v-2.07l-.5-.34A5 5 0 0 1 12 4a5 5 0 0 1 2.5 9.59z" />
    </svg>
  );
}

/** Target/bullseye — Purpose */
function IconPurpose() {
  return (
    <svg viewBox="0 0 24 24">
      <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm0 18a8 8 0 1 1 0-16 8 8 0 0 1 0 16zm0-14a6 6 0 1 0 0 12 6 6 0 0 0 0-12zm0 10a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm0-6a2 2 0 1 0 0 4 2 2 0 0 0 0-4z" />
    </svg>
  );
}

/** Warning triangle — Problems Addressed */
function IconProblems() {
  return (
    <svg viewBox="0 0 24 24">
      <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
    </svg>
  );
}

/** Puzzle piece — Solutions Used */
function IconSolutions() {
  return (
    <svg viewBox="0 0 24 24">
      <path d="M20.5 11H19V7a2 2 0 0 0-2-2h-4V3.5a2.5 2.5 0 0 0-5 0V5H4a2 2 0 0 0-2 2v3.8h1.5a2.5 2.5 0 0 1 0 5H2V20a2 2 0 0 0 2 2h3.8v-1.5a2.5 2.5 0 0 1 5 0V22H17a2 2 0 0 0 2-2v-4h1.5a2.5 2.5 0 0 0 0-5z" />
    </svg>
  );
}

/** Clock — Background */
function IconBackground() {
  return (
    <svg viewBox="0 0 24 24">
      <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm0 18a8 8 0 1 1 0-16 8 8 0 0 1 0 16zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67V7z" />
    </svg>
  );
}

/** Flask — Methodology */
function IconMethodology() {
  return (
    <svg viewBox="0 0 24 24">
      <path d="M9 3v5.5L4.06 17.11A2 2 0 0 0 5.82 20h12.36a2 2 0 0 0 1.76-2.89L15 8.5V3h1V1H8v2h1zm4 0v6l1.34 2H9.66L11 9V3h2zm-5.94 14L10.2 13h3.6l3.14 4H7.06z" />
    </svg>
  );
}

/** Star — Best Practices */
function IconBestPractices() {
  return (
    <svg viewBox="0 0 24 24">
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 21 12 17.27 5.82 21 7 14.14l-5-4.87 6.91-1.01L12 2zm0 3.5L9.91 9.64l-4.38.64 3.17 3.09-.75 4.35L12 15.4l3.95 2.32-.75-4.35 3.17-3.09-4.38-.64L12 5.5z" />
    </svg>
  );
}

/** Book — Lessons Learned */
function IconLessons() {
  return (
    <svg viewBox="0 0 24 24">
      <path d="M21 4H3a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h18a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1zM4 18V6h7v12H4zm16 0h-7V6h7v12zM6 8h3v1.5H6V8zm0 3h3v1.5H6V11zm9-3h3v1.5h-3V8zm0 3h3v1.5h-3V11z" />
    </svg>
  );
}

/** Retrospective — a looking-back / reflection glyph (DEV-1264). */
function IconRetrospective() {
  return (
    <svg viewBox="0 0 24 24">
      <path d="M12 5V1L7 6l5 5V7a6 6 0 1 1-6 6H4a8 8 0 1 0 8-8z" />
    </svg>
  );
}

/** Link chain — References */
function IconReferences() {
  return (
    <svg viewBox="0 0 24 24">
      <path d="M3.9 12A3.1 3.1 0 0 1 7 8.9h4V7H7a5 5 0 0 0 0 10h4v-1.9H7A3.1 3.1 0 0 1 3.9 12zM8 13h8v-2H8v2zm9-6h-4v1.9h4a3.1 3.1 0 0 1 0 6.2h-4V17h4a5 5 0 0 0 0-10z" />
    </svg>
  );
}

/** Document — generic/custom */
function IconCustom() {
  return (
    <svg viewBox="0 0 24 24">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm4 18H6V4h7v5h5v11zM8 13h8v1.5H8V13zm0 3h8v1.5H8V16z" />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/*  Icon resolver                                                      */
/* ------------------------------------------------------------------ */

interface IconConfig {
  icon: React.ReactNode;
  boxClass?: string;
}

/** Match a section to an icon. Prefers the typed section_type (stable
 *  across locales/renames); falls back to case-insensitive title match. */
function resolveIcon(title: string, sectionType?: string): IconConfig {
  // DEV-1264: retrospective gets a distinct glyph by type, not title.
  if (sectionType === 'retrospective') return { icon: <IconRetrospective /> };

  const t = title.toLowerCase().trim();

  if (t.includes('overview')) return { icon: <IconOverview /> };
  if (t.includes('purpose')) return { icon: <IconPurpose /> };
  if (t.includes('problem')) return { icon: <IconProblems />, boxClass: styles.iconBoxProblem };
  if (t.includes('solution')) return { icon: <IconSolutions />, boxClass: styles.iconBoxSolution };
  if (t.includes('background') || t.includes('history')) return { icon: <IconBackground /> };
  if (t.includes('methodolog')) return { icon: <IconMethodology /> };
  if (t.includes('best practice')) return { icon: <IconBestPractices /> };
  if (t.includes('lesson')) return { icon: <IconLessons /> };
  if (t.includes('reference') || t.includes('resource')) return { icon: <IconReferences /> };
  // Risks → warning-triangle (same SVG as Problems Addressed). Added
  // 2026-05-19 so Mini-Project's pre-seeded "Risks" section gets a
  // proper semantic icon instead of the generic document fallback.
  // Intentional: NOT tinted red — Risks here are a section topic
  // (write about your risks), not an active hazard signal.
  if (t.includes('risk')) return { icon: <IconProblems /> };

  return { icon: <IconCustom /> };
}

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

export interface ProjectContentSectionsProps {
  /** Legacy: when set without entityType, treated as entityType='project'.
   *  Existing CoreTabPanel callers can keep using projectGuid; new callers
   *  (Mini-Project AboutTabPanel) should pass entityType + entityGuid. */
  projectGuid?: string;
  /** Polymorphic entity context (migration 167). Defaults to 'project'. */
  entityType?: ContentSectionEntityType;
  /** Polymorphic entity GUID (migration 167). Falls back to projectGuid. */
  entityGuid?: string;
  canEdit: boolean;
  /** Page-level edit mode (controlled by parent toggle). */
  isEditing?: boolean;
  /**
   * DEV-1264. When true, a retrospective section is supported on this
   * entity: any `section_type='retrospective'` row renders last with
   * distinct treatment, and — when none exists yet — a "Write the
   * retrospective" prompt appears (independent of page edit mode).
   * Driven by the mini-project About panel as `status==='complete' &&
   * isLead`. Defaults off, so Full Project About is unaffected until
   * DEV-49 opts in.
   */
  retrospectiveAffordance?: boolean;
  /**
   * Which portion of the section set this instance renders. The About
   * surfaces (mini + full) mount this component twice:
   *   - slot="overview": renders ONLY the `section_type='overview'`
   *     section, in the lead position above Team. No "Background"
   *     heading, no add-section row. Renders nothing when no overview
   *     exists yet (it's added from the body block and floats up here).
   *   - slot="body" (default): renders every other section in the
   *     reorderable "Background" block (retrospective pinned last),
   *     with the heading and the add-section dropdown.
   * Keeping it one component preserves mini↔full parity (DEV-1265):
   * the same edit/add/reorder model serves both slots and both tiers.
   */
  slot?: 'overview' | 'body';
}

/* ------------------------------------------------------------------ */
/*  Single section panel                                               */
/* ------------------------------------------------------------------ */

interface SectionPanelProps {
  section: ProjectContentSectionResponse;
  canEdit: boolean;
  isEditing: boolean;
  defaultExpanded: boolean;
  /** DEV-1265: open the focused section-editor modal for this section.
   *  Replaces the old inline title/body editing. */
  onRequestEdit: (section: ProjectContentSectionResponse) => void;
  onDelete: (guid: string) => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  isFirst?: boolean;
  isLast?: boolean;
  t: (key: string) => string;
}

/** Lazily created once per module — shared across all SectionPanel instances. */
const sectionExtensions = getSectionExtensions();

/** Convert TipTap JSON string to HTML. Returns empty string on failure. */
function renderBodyHtml(bodyJson: string | null | undefined): string {
  if (!bodyJson) return '';
  try {
    const doc = JSON.parse(bodyJson);
    return generateHTML(doc, sectionExtensions);
  } catch {
    return '';
  }
}

function SectionPanel({
  section,
  canEdit,
  isEditing,
  defaultExpanded,
  onRequestEdit,
  onDelete,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
  t,
}: SectionPanelProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  // Pre-render HTML for view mode (memoized on bodyJson changes).
  const bodyHtml = useMemo(() => renderBodyHtml(section.bodyJson), [section.bodyJson]);

  const { icon, boxClass } = resolveIcon(section.title, section.sectionType);
  const isRetrospective = section.sectionType === 'retrospective';

  // DEV-1265: one-click Edit (modal) — available to editors WITHOUT the
  // page-level "Edit Page" toggle. Structural ops (reorder/delete) remain
  // behind the toggle (isEditing).
  const actions =
    canEdit || isEditing ? (
      <div className={styles.sectionActions}>
        {canEdit && (
          <button
            className={styles.actionBtn}
            onClick={(e) => { e.stopPropagation(); onRequestEdit(section); }}
            title={t('projectDetail.about.editSection')}
          >
            ✎
          </button>
        )}
        {isEditing && !isFirst && onMoveUp && (
          <button
            className={styles.actionBtn}
            onClick={(e) => { e.stopPropagation(); onMoveUp(); }}
            title={t('projectDetail.about.moveUp')}
          >
            ▲
          </button>
        )}
        {isEditing && !isLast && onMoveDown && (
          <button
            className={styles.actionBtn}
            onClick={(e) => { e.stopPropagation(); onMoveDown(); }}
            title={t('projectDetail.about.moveDown')}
          >
            ▼
          </button>
        )}
        {isEditing && (
          <button
            className={`${styles.actionBtn} ${styles.actionBtnDanger}`}
            onClick={(e) => { e.stopPropagation(); onDelete(section.sectionGuid); }}
            title={t('projectDetail.about.deleteSection')}
          >
            ✕
          </button>
        )}
      </div>
    ) : null;

  const badge = isRetrospective ? (
    <span className={styles.retrospectiveBadge}>
      {t('projectDetail.about.retrospectiveBadge')}
    </span>
  ) : null;

  // ── Expanded state ──
  if (expanded) {
    return (
      <div
        id={isRetrospective ? 'retrospective-section' : undefined}
        className={`${styles.section} ${styles.sectionExpanded}${isRetrospective ? ' ' + styles.sectionRetrospective : ''}`}
      >
        <div
          className={styles.sectionExpandedHeader}
          style={{ cursor: 'pointer' }}
          onClick={(e) => {
            if ((e.target as HTMLElement).closest('button, input, textarea, a')) return;
            setExpanded(false);
          }}
        >
          <div className={`${styles.iconBox} ${boxClass || ''}`}>{icon}</div>
          <div className={styles.sectionContent}>
            <div className={styles.sectionHeader}>
              <h4 className={styles.sectionTitle}>
                {section.title}
                {badge}
              </h4>
              {actions}
            </div>
          </div>
        </div>

        {/* Body content (view only — editing happens in the modal). */}
        <div className={styles.expandedBody}>
          {bodyHtml ? (
            <div
              className={styles.renderedBody}
              dangerouslySetInnerHTML={{ __html: sanitizeEmbed(bodyHtml) }}
            />
          ) : section.bodyPlain ? (
            <div>{section.bodyPlain}</div>
          ) : (
            <div
              style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}
              onClick={canEdit ? () => onRequestEdit(section) : undefined}
            >
              {canEdit ? t('projectDetail.about.emptyBodyPlaceholder') : ''}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Collapsed state (preview) ──
  return (
    <div
      id={isRetrospective ? 'retrospective-section' : undefined}
      className={`${styles.section}${isRetrospective ? ' ' + styles.sectionRetrospective : ''}`}
      style={{ cursor: 'pointer' }}
      onClick={() => setExpanded(true)}
    >
      <div className={`${styles.iconBox} ${boxClass || ''}`}>{icon}</div>
      <div className={styles.sectionContent}>
        <div className={styles.sectionHeader}>
          <h4 className={styles.sectionTitle}>{section.title}</h4>
          {badge}
          {actions}
        </div>

        {section.bodyPlain && (
          <div className={styles.preview}>{section.bodyPlain}</div>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

export default function ProjectContentSections({
  projectGuid,
  entityType,
  entityGuid,
  canEdit,
  isEditing: isEditingProp,
  retrospectiveAffordance = false,
  slot = 'body',
}: ProjectContentSectionsProps) {
  const { t } = useAppTranslation('pages');
  const isEditing = !!(isEditingProp && canEdit);
  const isOverviewSlot = slot === 'overview';
  const [creatingRetro, setCreatingRetro] = useState(false);

  // Normalize entity context. New callers pass entityType + entityGuid;
  // legacy CoreTabPanel call still passes projectGuid. Migration 167.
  const effectiveEntityType: ContentSectionEntityType = entityType ?? 'project';
  const effectiveEntityGuid: string | undefined = entityGuid ?? projectGuid;

  const {
    sections,
    loading,
    addSection,
    removeSection,
    reorder,
  } = useApiProjectSections(effectiveEntityType, effectiveEntityGuid);
  const [showDropdown, setShowDropdown] = useState(false);
  // DEV-1265: the section being edited in the focused modal (null = closed).
  const [editorSection, setEditorSection] =
    useState<ProjectContentSectionResponse | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // The Overview is rendered in its own lead slot above Team (typed via
  // section_type so it's stable, not title-matched). Identify it here so
  // both the overview slot (renders it) and the body slot (excludes it)
  // agree on which row it is.
  const overviewSection = useMemo(
    () => sections.find((s) => s.sectionType === 'overview') ?? null,
    [sections],
  );
  const hasOverview = !!overviewSection;

  // Body block ordering. The overview is pulled out to its own slot; the
  // retrospective (DEV-1264) always renders LAST regardless of its stored
  // sort_order (it's a completion narrative, not background).
  const orderedSections = useMemo(() => {
    const retro = sections.filter((s) => s.sectionType === 'retrospective');
    const rest = sections.filter(
      (s) => s.sectionType !== 'retrospective' && s.sectionType !== 'overview',
    );
    return [...rest, ...retro];
  }, [sections]);
  const hasRetrospective = orderedSections.some(
    (s) => s.sectionType === 'retrospective',
  );

  // DEV-1264: create the retrospective section on demand (completion
  // affordance). Title is localized; section_type pins identity.
  const handleCreateRetrospective = useCallback(async () => {
    if (creatingRetro) return;
    setCreatingRetro(true);
    const nextOrder = sections.length > 0
      ? Math.max(...sections.map((s) => s.sortOrder)) + 1
      : 0;
    try {
      await addSection({
        title: t('projectDetail.about.retrospectiveTitle'),
        sectionType: 'retrospective',
        sortOrder: nextOrder,
        isVisible: true,
      });
    } catch (err) {
      console.error('[ContentSections] create retrospective failed', err);
    } finally {
      setCreatingRetro(false);
    }
  }, [addSection, sections, t, creatingRetro]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!showDropdown) return;
    function handleClick(e: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showDropdown]);

  const handleAddSection = useCallback(
    async (titleKey: string) => {
      setShowDropdown(false);
      const title = t(`projectDetail.about.${titleKey}`);
      const sectionType = TITLE_KEY_SECTION_TYPE[titleKey];
      const nextOrder = sections.length > 0
        ? Math.max(...sections.map((s) => s.sortOrder)) + 1
        : 0;
      try {
        await addSection({
          title,
          // Self-tag predefined sections so typed slots (overview) and
          // the templatize/knowledge layer can find them by identity.
          ...(sectionType ? { sectionType } : {}),
          sortOrder: nextOrder,
          isVisible: true,
        });
      } catch (err) {
        console.error('[ContentSections] add failed', err);
      }
    },
    [addSection, sections, t]
  );

  const handleAddCustom = useCallback(async () => {
    setShowDropdown(false);
    const title = window.prompt(
      t('projectDetail.about.sectionTitlePlaceholder')
    );
    if (!title?.trim()) return;
    const nextOrder = sections.length > 0
      ? Math.max(...sections.map((s) => s.sortOrder)) + 1
      : 0;
    try {
      await addSection({
        title: title.trim(),
        sortOrder: nextOrder,
        isVisible: true,
      });
    } catch (err) {
      console.error('[ContentSections] add custom failed', err);
    }
  }, [addSection, sections, t]);

  const handleReorder = useCallback(
    async (index: number, direction: 'up' | 'down') => {
      const swapIdx = direction === 'up' ? index - 1 : index + 1;
      // Reorder operates on the DISPLAYED order; the retrospective is
      // pinned last and never participates (its move arrows are
      // suppressed below), so a swap can't cross it.
      if (swapIdx < 0 || swapIdx >= orderedSections.length) return;
      if (orderedSections[swapIdx]?.sectionType === 'retrospective') return;
      const reordered = [...orderedSections];
      [reordered[index], reordered[swapIdx]] = [reordered[swapIdx], reordered[index]];
      const items = reordered.map((s, i) => ({
        sectionGuid: s.sectionGuid,
        sortOrder: i,
      }));
      try {
        await reorder(items);
      } catch (err) {
        console.error('[ContentSections] reorder failed', err);
      }
    },
    [orderedSections, reorder]
  );

  const handleDelete = useCallback(
    (sectionGuid: string) => {
      if (!window.confirm(t('projectDetail.about.deleteSectionConfirm'))) {
        return;
      }
      removeSection(sectionGuid).catch((err) =>
        console.error('[ContentSections] delete failed', err)
      );
    },
    [removeSection, t]
  );

  if (loading) return null;

  // ── Overview slot ──────────────────────────────────────────────────
  // Renders only the typed overview section, in the lead position above
  // Team. Nothing to show until an Overview exists — it's created from
  // the body block's "+ Add section" dropdown and floats up here once
  // tagged. No heading, no add row: it reads as the page's opening
  // narrative, mirroring how the retrospective is pinned to the end.
  if (isOverviewSlot) {
    if (!overviewSection) return null;
    return (
      <div className={styles.wrapper}>
        <SectionPanel
          key={overviewSection.sectionGuid}
          section={overviewSection}
          canEdit={canEdit}
          isEditing={isEditing}
          defaultExpanded
          onRequestEdit={setEditorSection}
          onDelete={handleDelete}
          isFirst
          isLast
          t={t}
        />
        {editorSection && (
          <SectionEditorModal
            entityType={effectiveEntityType}
            entityGuid={effectiveEntityGuid || ''}
            sectionGuid={editorSection.sectionGuid}
            allowTitleEdit={false}
            onClose={() => setEditorSection(null)}
          />
        )}
      </div>
    );
  }

  // ── Body slot ──────────────────────────────────────────────────────
  // Hide the whole "Background" block when the only content lives in the
  // overview slot (so we don't show an empty heading). Stay rendered when
  // the retrospective affordance is on so its prompt can appear.
  if (orderedSections.length === 0 && !isEditing && !retrospectiveAffordance) return null;

  return (
    <div className={styles.wrapper}>
      {/* ── Background section heading ── */}
      <h3 className={styles.backgroundTitle}>
        {t('projectDetail.about.backgroundTitle')}
      </h3>

      {/* ── Section panels ── */}
      {orderedSections.length === 0 && isEditing && (
        <div className={styles.empty}>
          {t('projectDetail.about.noSections')}
        </div>
      )}

      {orderedSections.map((s, idx) => {
        const isRetro = s.sectionType === 'retrospective';
        return (
          <SectionPanel
            key={s.sectionGuid}
            section={s}
            canEdit={canEdit}
            isEditing={isEditing}
            defaultExpanded={
              s.title.toLowerCase().includes('overview')
            }
            onRequestEdit={setEditorSection}
            onDelete={handleDelete}
            // Retrospective is pinned last — no reordering across it.
            onMoveUp={isRetro ? undefined : () => handleReorder(idx, 'up')}
            onMoveDown={isRetro ? undefined : () => handleReorder(idx, 'down')}
            isFirst={idx === 0}
            isLast={isRetro || idx === orderedSections.length - 1}
            t={t}
          />
        );
      })}

      {/* ── Retrospective create prompt (completion affordance) ──
          Shown when supported and not yet written. Independent of page
          edit mode — writing a retrospective is a completion action. */}
      {retrospectiveAffordance && !hasRetrospective && (
        <button
          type="button"
          className={styles.retrospectivePrompt}
          onClick={handleCreateRetrospective}
          disabled={creatingRetro}
        >
          <span className={styles.retrospectivePromptIcon}>
            <IconRetrospective />
          </span>
          <span className={styles.retrospectivePromptText}>
            <strong>{t('projectDetail.about.retrospectivePromptTitle')}</strong>
            <span>{t('projectDetail.about.retrospectivePromptHint')}</span>
          </span>
        </button>
      )}

      {/* ── Add Section button + dropdown (below sections) ── */}
      {isEditing && (
        <div className={styles.addRow} ref={dropdownRef}>
          <button
            className={styles.addBtn}
            onClick={() => setShowDropdown((s) => !s)}
          >
            <span style={{ fontSize: 18 }}>+</span>{' '}
            {t('projectDetail.about.addSection')}
          </button>

          {showDropdown && (
            <div className={styles.dropdown}>
              {SUGGESTED_TITLES
                // Enforce a single Overview — once one exists it lives in
                // the top slot, so drop it from the add menu.
                .filter((key) => !(key === 'overview' && hasOverview))
                .map((key) => (
                  <button
                    key={key}
                    className={styles.dropdownItem}
                    onClick={() => handleAddSection(key)}
                  >
                    {t(`projectDetail.about.${key}`)}
                  </button>
                ))}
              <div className={styles.dropdownDivider} />
              <button
                className={styles.dropdownItem}
                onClick={handleAddCustom}
              >
                {t('projectDetail.about.customTitle')}
              </button>
            </div>
          )}
        </div>
      )}

      {/* DEV-1265: focused section-editor modal — replaces inline editing.
          Opens for the section the user clicked Edit on. */}
      {editorSection && (
        <SectionEditorModal
          entityType={effectiveEntityType}
          entityGuid={effectiveEntityGuid || ''}
          sectionGuid={editorSection.sectionGuid}
          allowTitleEdit={!isPredefinedTitle(editorSection.title)
            && editorSection.sectionType === 'custom'}
          onClose={() => setEditorSection(null)}
        />
      )}
    </div>
  );
}
